import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  forwardRef,
  type MutableRefObject,
  useCallback,
  type KeyboardEventHandler,
} from "react";
import { useShallow } from "zustand/react/shallow";
import { useEditor, EditorContent, ReactRenderer } from "@tiptap/react";
import { mergeAttributes, type Editor, type Range } from "@tiptap/core";
import type { EditorState } from "@tiptap/pm/state";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import Placeholder from "@tiptap/extension-placeholder";
import Mention, { MentionPluginKey } from "@tiptap/extension-mention";
import { PastedContent } from "./pasted-content-extension";
import { countWords, makeId } from "src/Utils";
import { cn } from "src/lib/cn";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { serializeTiptapToMarkup, parseMarkupToTiptap } from "./tiptap-serialization";
import type { MentionItem } from "./mentionSearch";
import { filterMentionItems } from "./mentionSearch";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { useProjectStore } from "src/stores/projectStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { useSeriesStore } from "src/stores/seriesStore";
import { useMentionComposerSeriesDocsStore } from "src/stores/mentionComposerSeriesDocsStore";
import { useAllFoldersStore } from "../../AppHome/store.js";
import { useStructuredOutlineStore } from "src/components/StoryBible/StructuredOutline/StructuredOutlineStore";
import Auth from "src/Auth";
import Project from "src/models/Project";
import { AnimatePresence, motion } from "framer-motion";
import SVG from "react-inlinesvg";
import { Text as SudoText } from "src/sudokit/components/Text";
import { buildLabelToMentionItemsMap, extendLabelMapWithHarvestedStoryBibleTokens, findAutoMentionMatchesInPlainText } from "./autoEntityMentions";
import { shouldCreatePastedContentAttachment } from "@sudowrite/shared-chat";
import { mentionClickBridge } from "./mentionClickBridge";
import { navigateToEntityMention } from "./navigateToEntityMention";
import { publishAgenticChatMentionUsedFromItem } from "./mentionAnalytics";
import { getHoveredMenuRowIndex, syncMenuSelectionToHoveredRow } from "./menuSelection";
import { enrichDocumentsWithSeriesBookTitles } from "./enrichDocumentsWithSeriesBookTitles";
import type { MentionSeriesFolderBook } from "./resolveSeriesBooksFromFolderProjects";
import { resolveSeriesBooksFromFolderProjects } from "./resolveSeriesBooksFromFolderProjects";
import { resolveSeriesBooksForMentions } from "./resolveSeriesBooksForMentions";
import "./MentionTiptapEditor.css";

type MentionDocumentRow = { id: string; title?: string; bookTitle?: string };
type MentionSeriesDocumentRow = { id: string; title?: string; bookProjectId: string; bookTitle?: string };

const MENTION_ICON_MAP: Record<string, string> = {
  character: "/assets/character-icon.svg",
  worldbuilding: "/assets/world.svg",
  document: "/assets/document.svg",
  outline: "/assets/story-bible/outline-icon.svg",
  folder: "/assets/single-folder.svg",
  series: "/assets/series.svg",
};

/** Outer padding aligned with `@` MentionSuggestionsContainer (`p-2`) so chip spacing matches the legacy menu. */
const MENTION_MENU_SURFACE_CLASS = "bg-app-menu-color backdrop-blur-xl shadow-card border border-solid border-app-menu-color-hover rounded-2xl overflow-hidden p-2";
const MENTION_MENU_ITEM_BASE_CLASS = "sudokit use-component-styles flex items-center gap-2 w-[16rem] py-[9.5px] px-[12px] cursor-pointer transition-all text-left text-app-grey-0";
const MENTION_MENU_ITEM_ACTIVE_CLASS = "bg-[var(--menu-color-hover)] rounded-xl";
const MENTION_MENU_ITEM_INACTIVE_CLASS = "active:bg-app-menu-color-active active:scale-[0.99]";
/** Match composer selector chips (`text-app-primary-cta-high-contrast` in ChatComposer). */
const MENTION_CHIP_CLASS = "mention-node text-app-primary-cta-high-contrast";

/**
 * TipTap's suggestion `view.update` awaits `items()` before calling `onStart`. If the user dismisses the
 * trigger (e.g. backspace) before that await resolves, a stale continuation can still call `onStart`
 * after `onExit` — use live plugin state + range to skip those opens only.
 */
function isStaleSuggestionStartProps(editor: Editor, props: { range: Range }) {
  const st = MentionPluginKey.getState(editor.state) as
    | { active?: boolean; query?: string | null; range?: { from: number; to: number } }
    | undefined;
  if (!props?.range) return true;
  if (!st?.active) return true;
  return st.range?.from !== props.range.from || st.range?.to !== props.range.to;
}

/** Skip `onUpdate` only when the suggestion session has ended — do not compare `range` to live state. */
function isSuggestionPluginInactive(editor: Editor) {
  return !isMentionSuggestionPluginActive(editor.state);
}

/** Source of truth for whether the #-mention suggestion is active (do not rely on `suggestionOpenRef` alone). */
function isMentionSuggestionPluginActive(state: EditorState) {
  return !!(MentionPluginKey.getState(state) as { active?: boolean } | undefined)?.active;
}

/** Live `#` query from the mention plugin (still valid when blur cleared our React-side item refs). */
function liveMentionSuggestionQuery(state: EditorState): string {
  const st = MentionPluginKey.getState(state) as { query?: string | null } | undefined;
  return typeof st?.query === "string" ? st.query : "";
}

/**
 * Enter confirms a # suggestion only when the portaled menu is visible with items.
 * When the TipTap plugin stays active after Escape/blur teardown, Enter should submit instead
 * of inserting mention markup or falling through to a hard break (SW-9747).
 */
function isMentionSuggestionMenuConfirmable(
  suppressMentionMenu: boolean,
  items: MentionItem[],
  ownerDocument: Document,
): boolean {
  if (suppressMentionMenu) return false;
  if (items.length === 0) return false;
  return !!ownerDocument.querySelector("[data-mention-suggestion-popup]");
}

function nudgeMentionSuggestionDom(current: Editor | null) {
  if (!current?.view) return;
  try {
    current.chain().focus().run();
    if (!editorViewHasFocus(current)) {
      (current.view.dom as HTMLElement | undefined)?.focus?.({ preventScroll: true });
    }
    current.view.dispatch(current.state.tr);
  } catch {
    /* focus / noop dispatch races are non-fatal on blur teardown (jsdom / CI) */
  }
}

/** After Escape + toolbar reopen, a single microtask nudge can miss `view.update` under jsdom / busy CI runners. */
function scheduleMentionSuggestionDomNudges(getEditor: () => Editor | null) {
  queueMicrotask(() => {
    nudgeMentionSuggestionDom(getEditor());
    requestAnimationFrame(() => {
      nudgeMentionSuggestionDom(getEditor());
    });
  });
}

/** Ignore async suggestion updates that no longer match the current plugin query. */
function isStaleSuggestionUpdateProps(editor: Editor, props: { query?: string | null }) {
  const st = MentionPluginKey.getState(editor.state) as { active?: boolean; query?: string | null } | undefined;
  if (!st?.active) return true;
  const liveQuery = typeof st.query === "string" ? st.query : "";
  const propsQuery = typeof props.query === "string" ? props.query : "";
  return liveQuery !== propsQuery;
}

function useMentionSeriesBooks(project: { id?: string; seriesId?: string | null } | null | undefined): MentionSeriesFolderBook[] {
  const sudowriteUserId = useSudowriteStore((state) => state.userData?.userId);
  const folderStoreSlices = useAllFoldersStore(
    useShallow((state) => ({
      foldersStoreUserId: state.userId,
      foldersByIdBase: state.foldersById,
      optimisticFoldersById: state.optimisticUpdateFoldersById,
      projectsByIdBase: state.projectsById,
      optimisticProjectsById: state.optimisticUpdateProjectsById,
    })),
  );
  const seriesStoreContents = useSeriesStore((state) => state.contents);
  const storeSeriesId = useSeriesStore((state) => state.currentSeriesId);

  const foldersProjectsSnapshot = useMemo(() => {
    if (!sudowriteUserId || folderStoreSlices.foldersStoreUserId !== sudowriteUserId) {
      return { foldersById: {}, projectsById: {} };
    }
    return {
      foldersById: Object.assign({}, folderStoreSlices.foldersByIdBase, folderStoreSlices.optimisticFoldersById),
      projectsById: Object.assign({}, folderStoreSlices.projectsByIdBase, folderStoreSlices.optimisticProjectsById),
    };
  }, [sudowriteUserId, folderStoreSlices]);

  const seriesBooksFromFolders = useMemo(
    () =>
      resolveSeriesBooksFromFolderProjects(project?.id, foldersProjectsSnapshot.foldersById, foldersProjectsSnapshot.projectsById),
    [project?.id, foldersProjectsSnapshot.foldersById, foldersProjectsSnapshot.projectsById],
  );

  const seriesBooksFromSeriesStore = useMemo(
    () => resolveSeriesBooksForMentions(project, seriesStoreContents, storeSeriesId),
    [project, seriesStoreContents, storeSeriesId],
  );

  /** Prefer Postgres folder map when it lists multiple books — survives `resetSeries()` on home navigation. */
  return useMemo((): MentionSeriesFolderBook[] => {
    if (seriesBooksFromFolders.length > 1) {
      return seriesBooksFromFolders;
    }
    if (seriesBooksFromSeriesStore.length > 0) {
      return seriesBooksFromSeriesStore;
    }
    return seriesBooksFromFolders;
  }, [seriesBooksFromFolders, seriesBooksFromSeriesStore]);
}

function getProjectDocumentRows(result: unknown): Array<{ id?: string; title?: string; type?: string }> {
  if (!result || typeof result !== "object") return [];
  const response = result as {
    contents?: unknown;
    project?: { contents?: { contents?: unknown } | unknown };
  };
  if (Array.isArray(response.contents)) {
    return response.contents as Array<{ id?: string; title?: string; type?: string }>;
  }
  if (response.project?.contents && typeof response.project.contents === "object") {
    const nested = (response.project.contents as { contents?: unknown }).contents;
    if (Array.isArray(nested)) {
      return nested as Array<{ id?: string; title?: string; type?: string }>;
    }
  }
  return [];
}

function editorViewHasFocus(ed: Editor): boolean {
  try {
    return typeof ed.view?.hasFocus === "function" && ed.view.hasFocus();
  } catch {
    return false;
  }
}

/**
 * TipTap's `#` mention trigger only fires when `#` is at a "word break" (start of inline run / after whitespace).
 * Mirror that when inserting from the toolbar so `hello` + picker → `hello #` not `hello#`.
 */
function mentionTriggerInsertionForCursor(ed: Editor): string {
  const pos = ed.state.selection.from;
  if (pos < 1) return "#";
  const before = ed.state.doc.textBetween(pos - 1, pos, "", "");
  /** Cursor immediately after `#` → trigger already exists (e.g. after Escape); inserting ` #` would yield `# #` and empty matches. */
  if (before === "#") return "";
  if (!before || /\s/u.test(before)) return "#";
  return " #";
}

/**
 * TipTap Mention's default `options.renderHTML` / `options.renderText` prepend `options.suggestion.char`
 * (default `@`). Overriding those in `addOptions()` is what actually drives the editor DOM — a top-level
 * `extend({ renderHTML })` does not replace `options.renderHTML`, so the chip kept showing `@` + label.
 */
const ExtendedMention = Mention.extend({
  addAttributes() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      bookTitle: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-book-title"),
        renderHTML: (attributes: { bookTitle?: string | null }) => {
          const bookTitle = typeof attributes.bookTitle === "string" ? attributes.bookTitle.trim() : "";
          return bookTitle ? { "data-book-title": bookTitle } : {};
        },
      },
    };
  },

  addOptions() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      suggestion: {
        ...(parent as { suggestion?: Record<string, unknown> }).suggestion,
        char: "#",
      },
      renderHTML({ options, node }) {
        const id = String(node.attrs.id ?? "");
        const label = String(node.attrs.label ?? node.attrs.id ?? "");
        const bookTitle = typeof node.attrs.bookTitle === "string" ? node.attrs.bookTitle.trim() : "";
        const displayLabel = bookTitle && !label.endsWith(` (${bookTitle})`) ? `${label} (${bookTitle})` : label;
        return [
          "span",
          mergeAttributes(
            {
              "data-type": "mention",
              "data-mention-id": id,
              ...(bookTitle ? { "data-book-title": bookTitle } : {}),
              class: MENTION_CHIP_CLASS,
            },
            options.HTMLAttributes,
          ),
          `#${displayLabel}`,
        ];
      },
      renderText({ node }) {
        const label = String(node.attrs.label ?? node.attrs.id ?? "");
        const bookTitle = typeof node.attrs.bookTitle === "string" ? node.attrs.bookTitle.trim() : "";
        const displayLabel = bookTitle && !label.endsWith(` (${bookTitle})`) ? `${label} (${bookTitle})` : label;
        return `#${displayLabel}`;
      },
    };
  },
});

type MentionSuggestionListProps = {
  items: MentionItem[];
  command: (item: MentionItem) => void;
  selectedIndex: number | null;
  /** Keep keyboard selection aligned with the row under the pointer so Enter matches click/hover. */
  onSelectedIndexChange?: (index: number) => void;
};

function MentionSuggestionList({ items, command, selectedIndex, onSelectedIndexChange }: MentionSuggestionListProps) {
  const safeSelectedIndex = !items.length ? null : Math.min(Math.max(0, selectedIndex ?? 0), items.length - 1);
  const listboxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (safeSelectedIndex == null) return;
    const listbox = listboxRef.current;
    if (!listbox) return;
    const activeRow = listbox.querySelector(`[data-mention-index="${safeSelectedIndex}"]`) as HTMLElement | null;
    if (!activeRow) return;
    activeRow.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [safeSelectedIndex, items.length]);

  return (
    <WithReset>
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className={MENTION_MENU_SURFACE_CLASS}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      >
        <WithReset
          ref={listboxRef}
          className="flex flex-col overflow-y-auto"
          style={{ maxHeight: "380px" }}
          role="listbox"
          aria-label="Mention suggestions"
        >
          {items.map((item, index) => {
            const iconSrc = MENTION_ICON_MAP[item.type] || "/assets/document.svg";
            return (
              <WithReset
                key={item.id}
                as="button"
                type="button"
                role="option"
                aria-selected={index === safeSelectedIndex}
                data-mention-index={index}
                style={{ color: "var(--grey-0)" }}
                className={cn(
                  MENTION_MENU_ITEM_BASE_CLASS,
                  // Only the active index is filled — no CSS :hover on other rows or Enter can disagree
                  // with a resting pointer. Moving the mouse updates selection via onMouseEnter.
                  index === safeSelectedIndex ? MENTION_MENU_ITEM_ACTIVE_CLASS : MENTION_MENU_ITEM_INACTIVE_CLASS,
                )}
                onMouseEnter={() => {
                  onSelectedIndexChange?.(index);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  command(item);
                }}
              >
                <WithReset className="w-6 h-6 flex items-center justify-center shrink-0 self-start mt-0.5">
                  <SVG
                    src={iconSrc}
                    className="w-5 h-5 [&_path]:fill-app-grey-0"
                  />
                </WithReset>
                <WithReset className="min-w-0 flex flex-col gap-0.5 flex-1 text-left">
                  <SudoText className="truncate leading-snug">{item.display}</SudoText>
                  {item.subtitle?.trim() ? (
                    <SudoText
                      size="xs"
                      color="grey-1"
                      className="truncate leading-tight"
                    >
                      {item.subtitle.trim()}
                    </SudoText>
                  ) : null}
                </WithReset>
              </WithReset>
            );
          })}
          {items.length === 0 && (
            <WithReset className="py-2 px-3">
              <SudoText
                size="sm"
                color="grey-1"
              >
                No matches
              </SudoText>
            </WithReset>
          )}
        </WithReset>
      </motion.div>
    </WithReset>
  );
}

export type MentionTiptapEditorHandle = {
  focus: () => void;
  getMarkup: () => string;
  insertMention: (item: MentionItem) => void;
  /** Runs ProseMirror's plain-text paste path (handles custom `handlePaste` + default insertion reliably in tests). */
  pastePlainText: (text: string) => void;
  /** Inserts `#` (or focuses an open picker) so the mention suggestion menu appears */
  openMentionPicker: () => void;
  clear: () => void;
};

export { shouldCreatePastedContentAttachment };

type MentionTiptapEditorProps = {
  value: string;
  onChange: (markup: string) => void;
  onSubmit: () => void;
  onPasteLongText?: (text: string, id: string, wordCount: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  canSubmit?: boolean;
  /** Plain text of the document (mentions as labels) for shortcuts while streaming */
  plainTextForShortcuts?: string;
  /** When set, Enter runs this instead of submit (e.g. stop the agent) */
  onSubmitShortcutOverride?: () => void;
  className?: string;
  bottomControls?: React.ReactNode;
  onKeyDownCapture?: KeyboardEventHandler<HTMLDivElement>;
};

export const MentionTiptapEditor = forwardRef<MentionTiptapEditorHandle, MentionTiptapEditorProps>(
  ({ value, onChange, onSubmit, onPasteLongText, onFocus: onParentFocus, onBlur: onParentBlur, placeholder, disabled: _disabled, canSubmit, plainTextForShortcuts, onSubmitShortcutOverride, className, bottomControls, onKeyDownCapture }, ref) => {
    const characters = useStoryBibleStore((state) => state.charactersEntities);
    const worldbuilding = useStoryBibleStore((state) => state.worldbuildingEntries);
    const userId = useSudowriteStore((state) => state.userData?.userId);
    const documentsRaw = useProjectStore((state) => state.contents as MentionDocumentRow[] | undefined);
    const folders = useProjectStore((state) => state.project?.folders ?? []);
    const project = useProjectStore((state) => state.project);
    const getContentsBySeriesId = useSeriesStore((state) => state.getContentsBySeriesId);
    const seriesBooks = useMentionSeriesBooks(project);

    useEffect(() => {
      const raw = project?.seriesId;
      const sid = typeof raw === "string" ? raw.trim() : "";
      if (!sid) return;
      void getContentsBySeriesId(sid);
    }, [project?.seriesId, getContentsBySeriesId]);

    const isStoryBibleActive = useStoryBibleStore((state) => state.isActive);
    const outlineItems = useStructuredOutlineStore((state) => state.outline?.content?.items);
    const hasOutline = outlineItems && outlineItems.length > 0;

    const documents = useMemo(
      () => enrichDocumentsWithSeriesBookTitles(documentsRaw ?? [], project, seriesBooks) as MentionDocumentRow[],
      [documentsRaw, seriesBooks, project],
    );

    // Load documents from series books for cross-book chapter mentions.
    const seriesBookDocuments = useMentionComposerSeriesDocsStore((s) => s.seriesBookDocuments as MentionSeriesDocumentRow[]);
    const setMentionComposerSeriesDocs = useMentionComposerSeriesDocsStore((s) => s.setSeriesBookDocuments);

    useEffect(() => {
      return () => {
        setMentionComposerSeriesDocs([]);
      };
    }, [setMentionComposerSeriesDocs]);

    useEffect(() => {
      if (!userId || !seriesBooks?.length) {
        setMentionComposerSeriesDocs([]);
        return;
      }
      let cancelled = false;
      async function loadSeriesDocuments() {
        const currentFirebaseId = project?.id ?? "";
        const siblingBooks = seriesBooks.filter((book) => {
          if (!book.projectId) return false;
          if (currentFirebaseId && book.projectId === currentFirebaseId) return false;
          return true;
        });
        const results = await Promise.allSettled(
          siblingBooks.map(async (book) => {
            const [result, metadata] = await Promise.all([
              Auth.fetchWithIdToken(`/api/projects/${book.projectId}`),
              // Keep chapters available with tree titles if metadata is temporarily unavailable.
              Project.getMetadata(userId, book.projectId).catch(() => ({})),
            ]);
            const docs: Array<{ id: string; title?: string; bookProjectId: string; bookTitle?: string }> = [];
            for (const doc of getProjectDocumentRows(result)) {
              if (doc.type && doc.type !== "document") continue;
              if (doc.id) {
                // Project tree titles can predate renames; use saved metadata like the current-book list.
                const savedTitle = metadata[doc.id]?.title;
                const docTitle = typeof savedTitle === "string" ? savedTitle : typeof doc.title === "string" ? doc.title : "";
                docs.push({
                  id: doc.id,
                  title: docTitle,
                  bookProjectId: book.projectId,
                  bookTitle: book.title || "Series Book",
                });
              }
            }
            return docs;
          }),
        );
        const allDocs = results.flatMap((result) => result.status === "fulfilled" ? result.value : []);
        if (!cancelled) {
          setMentionComposerSeriesDocs(allDocs);
        }
      }
      void loadSeriesDocuments();
      return () => {
        cancelled = true;
      };
    }, [seriesBooks, project?.id, userId, setMentionComposerSeriesDocs]);

    const mentionDataRef = useRef({
      characters,
      worldbuilding,
      documents,
      folders,
      isStoryBibleActive,
      hasOutline,
      seriesBooks,
      seriesBookDocuments,
    });
    mentionDataRef.current = {
      characters,
      worldbuilding,
      documents,
      folders,
      isStoryBibleActive,
      hasOutline,
      seriesBooks,
      seriesBookDocuments,
    };

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const onSubmitRef = useRef(onSubmit);
    onSubmitRef.current = onSubmit;

    const canSubmitRef = useRef(canSubmit);
    canSubmitRef.current = canSubmit;

    const plainTextForShortcutsRef = useRef(plainTextForShortcuts);
    plainTextForShortcutsRef.current = plainTextForShortcuts;

    const onSubmitShortcutOverrideRef = useRef(onSubmitShortcutOverride);
    onSubmitShortcutOverrideRef.current = onSubmitShortcutOverride;

    const isInternalUpdate = useRef(false);
    const suggestionOpenRef = useRef(false);
    /** Latest filtered mention items for the open # menu (TipTap's props.items can be stale). */
    const mentionSuggestionItemsRef = useRef<MentionItem[]>([]);
    /** Last #-query for the suggestion menu; used to reset selection only when the query changes. */
    const mentionSuggestionQueryRef = useRef<string>("");
    /**
     * Active mention index, updated synchronously on arrow keys / mouseEnter.
     * `null` means the user has not explicitly selected a row, so Enter should submit instead of inserting item 0.
     */
    const mentionSuggestionSelectedIndexRef = useRef<number | null>(null);
    /** Set when user clicks in the editor to dismiss the # menu; cleared on text input or natural exit. */
    const suppressMentionMenuRef = useRef(false);
    /** Clears mention-menu refs only (no DOM). Named so debugging is not confused with `null`/anonymous `fn.name`. */
    const clearMentionSuggestionRefsOnly = useCallback(() => {
      suggestionOpenRef.current = false;
      mentionSuggestionQueryRef.current = "";
      mentionSuggestionSelectedIndexRef.current = null;
      mentionSuggestionItemsRef.current = [];
    }, []);
    const closeMentionSuggestionRef = useRef<() => void>(clearMentionSuggestionRefsOnly);
    const editorRef = useRef<Editor | null>(null);
    const skipAutoMergeRef = useRef(false);

    const labelToMentionItems = useMemo(() => {
      const map = buildLabelToMentionItemsMap({
        characters,
        worldbuilding,
        documents: documents ?? [],
        seriesBooks,
        seriesBookDocuments,
        hasOutline: !!hasOutline,
        isStoryBibleActive,
      });
      extendLabelMapWithHarvestedStoryBibleTokens(map, characters, worldbuilding, documents ?? [], {
        isStoryBibleActive,
        seriesBooks,
        seriesBookDocuments,
      });
      return map;
    }, [characters, worldbuilding, documents, seriesBooks, seriesBookDocuments, hasOutline, isStoryBibleActive]);

    useEffect(() => {
      mentionClickBridge.current = navigateToEntityMention;
      return () => {
        mentionClickBridge.current = () => {};
      };
    }, []);

    const labelMapRef = useRef(labelToMentionItems);
    labelMapRef.current = labelToMentionItems;

    const applyAutoEntityMerge = useCallback((ed: Editor) => {
      if (!ed) return;
      // Sync with ProseMirror: this runs on every transaction while `suggestionOpenRef` can lag async onStart/onExit.
      if (skipAutoMergeRef.current || isMentionSuggestionPluginActive(ed.state)) {
        isInternalUpdate.current = true;
        onChangeRef.current(serializeTiptapToMarkup(ed.getJSON()));
        return;
      }
      const curMarkup = serializeTiptapToMarkup(ed.getJSON());
      isInternalUpdate.current = true;
      onChangeRef.current(curMarkup);
    }, []);

    /**
     * When the user presses Escape on the `#` menu, reopening with the **same query** is blocked until the
     * query changes (`updateSuggestionFromProps`). Clear this from `openMentionPicker` so the toolbar can reopen.
     */
    const escapeDismissedMentionQueryRef = useRef<string | null>(null);

    /**
     * After paste matching known entity/project names, bias the first plain Enter toward sending the composer
     * instead of implicitly confirming an inline picker (we only offer mentions via `#` or toolbar).
     */
    const preferSubmitOnEnterAfterPasteRef = useRef(false);

    const editor = useEditor({
      extensions: [
        Document,
        Paragraph,
        Text,
        HardBreak,
        History,
        Placeholder.configure({ placeholder: placeholder || "Ask a question about your project" }),
        PastedContent,
        ExtendedMention.configure({
          HTMLAttributes: {
            class: MENTION_CHIP_CLASS,
          },
          suggestion: {
            char: "#",
            allowSpaces: true,
            items: ({ query }) => {
              const { characters, worldbuilding, documents, folders, isStoryBibleActive, hasOutline, seriesBooks, seriesBookDocuments } = mentionDataRef.current;
              return filterMentionItems(query, {
                characters,
                worldbuilding,
                documents,
                folders,
                hasOutline,
                isStoryBibleActive,
                seriesBooks,
                seriesBookDocuments,
              });
            },
            render: () => {
              let component: ReactRenderer<any> | null = null;
              let popup: HTMLDivElement | null = null;
              const cleanupSuggestionPopup = () => {
                suggestionOpenRef.current = false;
                mentionSuggestionQueryRef.current = "";
                mentionSuggestionSelectedIndexRef.current = null;
                mentionSuggestionItemsRef.current = [];
                component?.destroy();
                component = null;
                if (popup) {
                  popup.remove();
                  popup = null;
                }
                closeMentionSuggestionRef.current = clearMentionSuggestionRefsOnly;
              };
              const setSuggestionSelectedIndex = (index: number) => {
                const len = mentionSuggestionItemsRef.current.length;
                const next = len ? Math.min(Math.max(0, index), len - 1) : 0;
                mentionSuggestionSelectedIndexRef.current = next;
                component?.updateProps({ selectedIndex: next });
              };
              const syncSelectionToHoveredRow = () => {
                syncMenuSelectionToHoveredRow(
                  popup,
                  "data-mention-index",
                  mentionSuggestionSelectedIndexRef.current,
                  setSuggestionSelectedIndex,
                );
              };
              const mountSuggestionPopup = (props: any, selectedIndex: number | null) => {
                if (!component) {
                  component = new ReactRenderer(MentionSuggestionListWrapper, {
                    props: {
                      ...props,
                      selectedIndex,
                      liveItemsRef: mentionSuggestionItemsRef,
                      onSelectedIndexChange: setSuggestionSelectedIndex,
                    },
                    editor: props.editor,
                  });
                }
                if (!popup) {
                  // Outer shell does not receive pointer events so `document.body` capture still sees
                  // clicks outside the visible menu (the inner div carries `data-mention-suggestion-popup`).
                  popup = document.createElement("div");
                  popup.style.position = "fixed";
                  popup.style.zIndex = "10000";
                  popup.style.pointerEvents = "none";

                  const hitSurface = document.createElement("div");
                  hitSurface.setAttribute("data-mention-suggestion-popup", "");
                  hitSurface.style.pointerEvents = "auto";
                  hitSurface.style.display = "inline-block";

                  document.body.appendChild(popup);
                  popup.appendChild(hitSurface);
                  hitSurface.appendChild(component.element);
                  closeMentionSuggestionRef.current = cleanupSuggestionPopup;
                }
              };
              const updateSuggestionFromProps = (props: any) => {
                const currentQuery = typeof props.query === "string" ? props.query : "";
                if (suppressMentionMenuRef.current) {
                  cleanupSuggestionPopup();
                  return;
                }
                const dismissed = escapeDismissedMentionQueryRef.current;
                if (dismissed !== null && currentQuery === dismissed) {
                  cleanupSuggestionPopup();
                  return;
                }
                if (dismissed !== null && currentQuery !== dismissed) {
                  escapeDismissedMentionQueryRef.current = null;
                }
                const queryChanged = mentionSuggestionQueryRef.current !== currentQuery;
                mentionSuggestionQueryRef.current = currentQuery;

                const tipItems = (props.items ?? []) as MentionItem[];
                mentionSuggestionItemsRef.current = tipItems;
                if (tipItems.length === 0) {
                  cleanupSuggestionPopup();
                  return;
                }
                const prevIdx = mentionSuggestionSelectedIndexRef.current;
                const nextSelected =
                  queryChanged || prevIdx == null
                    ? 0
                    : Math.min(Math.max(0, prevIdx), Math.max(0, tipItems.length - 1));
                mentionSuggestionSelectedIndexRef.current = nextSelected;

                mountSuggestionPopup(props, nextSelected);
                component?.updateProps({
                  ...props,
                  selectedIndex: nextSelected,
                  liveItemsRef: mentionSuggestionItemsRef,
                  onSelectedIndexChange: setSuggestionSelectedIndex,
                });
                if (popup) {
                  updatePopupPosition(popup, props);
                  requestAnimationFrame(syncSelectionToHoveredRow);
                }
              };

              return {
                onStart: (props: any) => {
                  if (isStaleSuggestionStartProps(props.editor, props)) {
                    return;
                  }
                  suppressMentionMenuRef.current = false;
                  suggestionOpenRef.current = true;
                  updateSuggestionFromProps(props);
                },
                onUpdate: (props) => {
                  if (isSuggestionPluginInactive(props.editor) || isStaleSuggestionUpdateProps(props.editor, props)) {
                    return;
                  }
                  updateSuggestionFromProps(props);
                },
                onKeyDown: ({ event }) => {
                  const arrowNavKey = event.key === "ArrowUp" || event.key === "ArrowDown";
                  // Any text input, arrow navigation, or backspace after a click-dismiss / blur resumes the menu
                  if (
                    suppressMentionMenuRef.current &&
                    (event.key === "Backspace" ||
                      event.key === "Delete" ||
                      arrowNavKey ||
                      (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey))
                  ) {
                    suppressMentionMenuRef.current = false;
                  }
                  let items = mentionSuggestionItemsRef.current;
                  // Blur sets suppress and tears down the popup while the suggestion plugin can stay active;
                  // items ref is then empty. A sync dispatch often repopulates on the next microtask/frame — defer
                  // arrow handling so the first ArrowUp/ArrowDown isn't lost (still return true to consume keys).
                  if (
                    items.length === 0 &&
                    arrowNavKey &&
                    editorRef.current &&
                    isMentionSuggestionPluginActive(editorRef.current.state)
                  ) {
                    const ed = editorRef.current;
                    const rebuildQuery = liveMentionSuggestionQuery(ed.state);
                    const {
                      characters,
                      worldbuilding,
                      documents,
                      folders,
                      isStoryBibleActive,
                      hasOutline,
                      seriesBooks,
                      seriesBookDocuments,
                    } = mentionDataRef.current;
                    const syncFiltered = filterMentionItems(rebuildQuery, {
                      characters,
                      worldbuilding,
                      documents,
                      folders,
                      hasOutline,
                      isStoryBibleActive,
                      seriesBooks,
                      seriesBookDocuments,
                    });
                    // Zero real matches ⇒ empty items are expected; do not swallow arrows (Bugbot).
                    if (syncFiltered.length === 0) {
                      return false;
                    }
                    suppressMentionMenuRef.current = false;
                    const keyPressed = event.key;
                    ed.view.dispatch(ed.state.tr);

                    const bumpSelectionFromRef = () => {
                      const current = editorRef.current;
                      if (!current?.view || !isMentionSuggestionPluginActive(current.state)) return;
                      const refreshed = mentionSuggestionItemsRef.current;
                      const len = refreshed.length;
                      if (len === 0) return;
                      const clamped = (idx: number) => Math.min(Math.max(0, idx), len - 1);
                      const base =
                        mentionSuggestionSelectedIndexRef.current == null ?
                          keyPressed === "ArrowUp" ? len : -1
                        : clamped(mentionSuggestionSelectedIndexRef.current);
                      const next =
                        keyPressed === "ArrowUp" ? Math.max(0, base - 1) : Math.min(len - 1, base + 1);
                      preferSubmitOnEnterAfterPasteRef.current = false;
                      mentionSuggestionSelectedIndexRef.current = next;
                      component?.updateProps({ selectedIndex: next });
                    };

                    queueMicrotask(() => {
                      bumpSelectionFromRef();
                      if (mentionSuggestionItemsRef.current.length > 0) return;
                      // Single follow-up frame (not a loop); items() can resolve after the noop transaction.
                      requestAnimationFrame(bumpSelectionFromRef);
                    });
                    return true;
                  }
                  if (event.key === "Escape") {
                    escapeDismissedMentionQueryRef.current = mentionSuggestionQueryRef.current || "";
                    closeMentionSuggestionRef.current();
                    return true;
                  }
                  if (event.key === "Backspace") {
                    const q = mentionSuggestionQueryRef.current;
                    if (!q) {
                      closeMentionSuggestionRef.current();
                      return false;
                    }
                  }
                  const len = items.length;
                  if (len === 0) {
                    return false;
                  }
                  const clamped = (idx: number) => Math.min(Math.max(0, idx), len - 1);
                  const current =
                    mentionSuggestionSelectedIndexRef.current == null ?
                      event.key === "ArrowUp" ? len : -1
                    : clamped(mentionSuggestionSelectedIndexRef.current);
                  if (event.key === "ArrowUp") {
                    preferSubmitOnEnterAfterPasteRef.current = false;
                    const next = Math.max(0, current - 1);
                    mentionSuggestionSelectedIndexRef.current = next;
                    component?.updateProps({ selectedIndex: next });
                    return true;
                  }
                  if (event.key === "ArrowDown") {
                    preferSubmitOnEnterAfterPasteRef.current = false;
                    const next = Math.min(len - 1, current + 1);
                    mentionSuggestionSelectedIndexRef.current = next;
                    component?.updateProps({ selectedIndex: next });
                    return true;
                  }
                  if (event.key === "Enter") {
                    const ownerDoc = editorRef.current?.view?.dom?.ownerDocument ?? document;
                    if (!isMentionSuggestionMenuConfirmable(suppressMentionMenuRef.current, items, ownerDoc)) {
                      return false;
                    }
                    preferSubmitOnEnterAfterPasteRef.current = false;
                    // Align keyboard selection with the hovered row before deciding submit vs pick (pointer-highlighted row had no keyboard index yet).
                    syncSelectionToHoveredRow();
                    let effectiveIndex = mentionSuggestionSelectedIndexRef.current;
                    if (effectiveIndex == null && popup) {
                      const hoveredIdx = getHoveredMenuRowIndex(popup, "data-mention-index");
                      if (hoveredIdx != null) {
                        mentionSuggestionSelectedIndexRef.current = hoveredIdx;
                        component?.updateProps({ selectedIndex: hoveredIdx });
                        effectiveIndex = hoveredIdx;
                      }
                    }
                    const index = clamped(effectiveIndex ?? 0);
                    const picked = items[index];
                    if (picked) {
                      (component?.props as any)?.command(picked);
                    }
                    return true;
                  }
                  return false;
                },
                onExit: (props: any) => {
                  // TipTap fires onExit for moved+changed updates even while suggestion remains active.
                  // Keep menu mounted through query edits/backspaces; fully clean up only on real exit.
                  if (props?.editor && isMentionSuggestionPluginActive(props.editor.state)) {
                    return;
                  }
                  suppressMentionMenuRef.current = false;
                  cleanupSuggestionPopup();
                },
              };
            },
            command: ({ editor, range, props: item }) => {
              const mentionItem = item as MentionItem;
              // Use live suggestion range from editor state — the closure's `range` can be stale on mouse pick.
              const pluginState = MentionPluginKey.getState(editor.state) as
                | { active?: boolean; range?: { from: number; to: number } }
                | undefined;
              let from = range.from;
              let to = range.to;
              if (pluginState?.active && pluginState.range && pluginState.range.to > pluginState.range.from) {
                from = pluginState.range.from;
                to = pluginState.range.to;
              }
              editor
                .chain()
                .focus()
                .insertContentAt(
                  { from, to },
                  { type: "mention", attrs: { id: mentionItem.id, label: mentionItem.display, bookTitle: mentionItem.subtitle } },
                )
                .run();

              publishAgenticChatMentionUsedFromItem(mentionItem);
            },
          },
        }),
      ],
      onCreate: ({ editor: created }) => {
        editorRef.current = created;
      },
      editorProps: {
        attributes: {
          class: "mention-tiptap-editor",
        },
        handleKeyDown: (view, event) => {
          const mentionActive = isMentionSuggestionPluginActive(view.state);
          if (mentionActive) {
            if (event.key === "Enter" && !event.shiftKey) {
              const ownerDoc = view.dom.ownerDocument ?? document;
              if (
                !isMentionSuggestionMenuConfirmable(
                  suppressMentionMenuRef.current,
                  mentionSuggestionItemsRef.current,
                  ownerDoc,
                )
              ) {
                preferSubmitOnEnterAfterPasteRef.current = false;
                event.preventDefault();
                if (canSubmitRef.current !== false) {
                  onSubmitRef.current();
                }
                return true;
              }
            }
            return false;
          }

          if (event.key === "Enter" && !event.shiftKey) {
            if (preferSubmitOnEnterAfterPasteRef.current) {
              preferSubmitOnEnterAfterPasteRef.current = false;
              event.preventDefault();
              if (canSubmitRef.current !== false) {
                onSubmitRef.current();
              }
              return true;
            }
            const override = onSubmitShortcutOverrideRef.current;
            if (override) {
              const plain = (plainTextForShortcutsRef.current ?? "").trim();
              if (plain.toLowerCase() === "stop") {
                event.preventDefault();
                override();
                return true;
              }
            }
            if (canSubmitRef.current !== false) {
              event.preventDefault();
              onSubmitRef.current();
              return true;
            }
          }
          return false;
        },
        handlePaste: (_view, event) => {
          const text = event.clipboardData?.getData("text/plain") || "";
          const shouldPreferSubmitOnEnter =
            text.trim().length > 0 &&
            findAutoMentionMatchesInPlainText(text, labelMapRef.current).length > 0;
          // Default: native/short pastes should not carry over "submit on Enter" from a prior paste.
          preferSubmitOnEnterAfterPasteRef.current = false;
          if (shouldCreatePastedContentAttachment(text)) {
            const currentEditor = editorRef.current;
            if (!currentEditor) return false;
            preferSubmitOnEnterAfterPasteRef.current = shouldPreferSubmitOnEnter;
            event.preventDefault();
            const wordCount = countWords(text);
            const id = makeId(12);
            currentEditor.chain().focus().insertContent([
              { type: "pastedContent", attrs: { id, wordCount: String(wordCount) } },
              { type: "text", text: " " },
            ]).run();
            onPasteLongText?.(text, id, wordCount);
            return true;
          }

          if (text.includes("#") || text.includes("@")) {
            const currentEditor = editorRef.current;
            if (!currentEditor) return false;
            const { characters, worldbuilding, documents, folders, isStoryBibleActive, hasOutline, seriesBooks, seriesBookDocuments } = mentionDataRef.current;
            const allItems = filterMentionItems("", { characters, worldbuilding, documents, folders, isStoryBibleActive, hasOutline, seriesBooks, seriesBookDocuments });
            if (allItems.length > 0) {
              const sortedItems = [...allItems].sort((a, b) => b.display.length - a.display.length);
              const contentNodes = parsePastedTextWithMentions(text, sortedItems);
              if (contentNodes.some((node) => node.type === "mention")) {
                preferSubmitOnEnterAfterPasteRef.current = shouldPreferSubmitOnEnter;
                event.preventDefault();
                currentEditor.chain().focus().insertContent(contentNodes).run();
                return true;
              }
            }
          }

          if (shouldPreferSubmitOnEnter) {
            // Native/short pastes: ProseMirror applies the paste after this returns false; set flag here (not
            // at the top) so a prior `true` is not left over when the browser ignores preventDefault.
            preferSubmitOnEnterAfterPasteRef.current = true;
          }
          return false;
        },
        handleDOMEvents: {
          mousedown: (_view, event) => {
            const target = event.target as HTMLElement | null;
            const el = target?.closest?.("[data-mention-id]") as HTMLElement | null;
            const id = el?.getAttribute("data-mention-id");
            if (id) {
              event.preventDefault();
              mentionClickBridge.current(id);
              return true;
            }
            return false;
          },
        },
      },
      onUpdate: ({ editor }) => {
        editorRef.current = editor;
        const mentionSuggestingActive = isMentionSuggestionPluginActive(editor.state);
        // Keep `suggestionOpenRef` aligned with ProseMirror — it used to drift from async onStart/onExit and
        // broke handleKeyDown / merge. Plugin state is the source of truth.
        if (!mentionSuggestingActive && suggestionOpenRef.current) {
          closeMentionSuggestionRef.current();
        }
        suggestionOpenRef.current = mentionSuggestingActive;
        applyAutoEntityMerge(editor);
      },
      onFocus: ({ editor: focusedEditor }) => {
        editorRef.current = focusedEditor;
        // Always clear suppress when focus returns: blur/pointer-dismiss can hide the UI while `#` is still open,
        // and suppress would otherwise block suggestion `onUpdate` from rebuilding the menu. Escape uses its own latch.
        suppressMentionMenuRef.current = false;
        queueMicrotask(() => {
          const current = editorRef.current;
          if (!current?.view) return;
          if (!editorViewHasFocus(current)) return;
          current.view.dispatch(current.state.tr);
        });
        onParentFocus?.();
      },
      onBlur: () => {
        if (suggestionOpenRef.current) {
          suppressMentionMenuRef.current = true;
          closeMentionSuggestionRef.current();
        }
        onParentBlur?.();
      },
    });

    useEffect(
      () => () => {
        editorRef.current = null;
      },
      [],
    );

    const dismissMentionSuggestionUi = useCallback(() => {
      const ed = editorRef.current;
      const pluginActive = ed ? isMentionSuggestionPluginActive(ed.state) : false;
      const ownerDoc = ed?.view?.dom?.ownerDocument ?? document;
      const popupInDom = ownerDoc.querySelector("[data-mention-suggestion-popup]");
      if (!pluginActive && !popupInDom) return;

      suppressMentionMenuRef.current = true;
      closeMentionSuggestionRef.current();

      if (pluginActive && ed) {
        queueMicrotask(() => {
          const current = editorRef.current;
          if (!current || !isMentionSuggestionPluginActive(current.state)) return;
          if (!editorViewHasFocus(current)) return;
          current.view.dispatch(current.state.tr);
        });
      }
    }, []);

    /**
     * Dismiss the portaled `#` suggestion popup when the user presses outside it.
     */
    useEffect(() => {
      const body = typeof document !== "undefined" ? document.body : null;
      if (!body) return undefined;

      const onPointerLikeDown = (event: MouseEvent | PointerEvent) => {
        const path = typeof event.composedPath === "function" ? event.composedPath() : [];
        const nodes: EventTarget[] = path.length > 0 ? path : [event.target ?? window];
        for (const n of nodes) {
          if (n instanceof Element && n.closest?.("[data-mention-suggestion-popup]")) {
            return;
          }
        }
        dismissMentionSuggestionUi();
      };

      body.addEventListener("pointerdown", onPointerLikeDown, true);
      body.addEventListener("mousedown", onPointerLikeDown, true);
      return () => {
        body.removeEventListener("pointerdown", onPointerLikeDown, true);
        body.removeEventListener("mousedown", onPointerLikeDown, true);
      };
    }, [dismissMentionSuggestionUi]);

    // When characters / documents load async, re-run merge on existing composer text.
    useEffect(() => {
      const ed = editorRef.current;
      if (ed) applyAutoEntityMerge(ed);
    }, [labelToMentionItems, applyAutoEntityMerge]);

    useEffect(() => {
      if (!editor) return;
      // Skip redundant serialize/compare when this update originated from the editor (typing).
      if (isInternalUpdate.current) {
        isInternalUpdate.current = false;
        if (value !== "") return;
      }
      const currentMarkup = serializeTiptapToMarkup(editor.getJSON());
      if (currentMarkup === value) {
        return;
      }
      // When the parent clears the composer (`value === ""`), always sync into TipTap. Otherwise a
      // trailing `isInternalUpdate` flag from paste/merge can skip `setContent` and leave stale text
      // after send (e.g. paste → Escape → Enter).
      isInternalUpdate.current = false;
      closeMentionSuggestionRef.current();
      const json = parseMarkupToTiptap(value);
      // emitUpdate: false prevents onUpdate from firing, avoiding feedback loops
      editor.commands.setContent(json, false);
    }, [value, editor]);

    useImperativeHandle(ref, () => ({
      focus: () => editor?.commands.focus(),
      getMarkup: () => (editor ? serializeTiptapToMarkup(editor.getJSON()) : ""),
      insertMention: (item: MentionItem) => {
        if (!editor) return;
        editor
          .chain()
          .focus()
          .insertContent({ type: "mention", attrs: { id: item.id, label: item.display, bookTitle: item.subtitle } })
          .run();

        publishAgenticChatMentionUsedFromItem(item);
      },
      pastePlainText: (textPayload: string) => {
        const ed = editorRef.current ?? editor;
        if (!ed?.view) return;

        /** ProseMirror + our `handlePaste` need real clipboard text; jsdom ClipboardEvent often omits helpers. */
        const pasteEvent = {
          type: "paste",
          bubbles: true,
          cancelable: true,
          defaultPrevented: false,
          clipboardData: {
            getData: (format: string) => (format === "text/plain" ? textPayload : ""),
          },
          preventDefault: () => {},
          stopPropagation: () => {},
        } as unknown as ClipboardEvent;
        ed.view.pasteText(textPayload, pasteEvent);
      },
      openMentionPicker: () => {
        const ed = editorRef.current ?? editor;
        if (!ed?.view) return;
        escapeDismissedMentionQueryRef.current = null;
        preferSubmitOnEnterAfterPasteRef.current = false;
        // Blur-dismiss can latch suppress without the plugin appearing "active" after Escape — toolbar open must bypass.
        suppressMentionMenuRef.current = false;
        if (isMentionSuggestionPluginActive(ed.state)) {
          ed.chain().focus().run();
          // TipTap suggestion only re-fetches `items` when plugin state / range / query changes in `view.update`.
          // A noop transaction can be ignored when state is unchanged. Always force one refresh cycle here so
          // Escape-dismiss + immediate toolbar reopen is deterministic in slower CI/jsdom timing.
          const snap = MentionPluginKey.getState(ed.state) as
            | { active?: boolean; range?: { from: number; to: number } }
            | undefined;
          const rg = snap?.range;
          if (snap?.active && rg && rg.to > rg.from) {
            const { from, to } = rg;
            const preserved = ed.state.doc.textBetween(from, to, "", "");
            ed.chain().focus().deleteRange({ from, to }).run();
            ed.chain().focus().insertContentAt(from, preserved).run();
            /** CI/jsdom can miss the suggestion `view.update` pass without this nudge after the transactional bounce. */
            scheduleMentionSuggestionDomNudges(() => editorRef.current ?? editor);
            return;
          }
          scheduleMentionSuggestionDomNudges(() => editorRef.current ?? editor);
          return;
        }
        const insertion = mentionTriggerInsertionForCursor(ed);
        // `insertion === ""` ⇒ cursor immediately after `#`. Escape/blur can leave the TipTap suggestion plugin
        // inactive — delete + re-insert `#` to reliably restart `#` mentions for the toolbar.
        if (insertion === "") {
          const pos = ed.state.selection.from;
          const fromHash = pos - 1;
          ed.chain().focus().deleteRange({ from: fromHash, to: pos }).insertContentAt(fromHash, "#").run();
        } else {
          ed.chain().focus().insertContent(insertion).run();
        }
        scheduleMentionSuggestionDomNudges(() => editorRef.current ?? editor);
      },
      clear: () => {
        if (!editor) {
          onChangeRef.current("");
          return;
        }
        closeMentionSuggestionRef.current();
        isInternalUpdate.current = false;
        editor.commands.setContent(parseMarkupToTiptap(""), false);
        onChangeRef.current("");
      },
    }), [editor]);

    return (
      <div
        data-agentic-chat-composer="true"
        onKeyDownCapture={onKeyDownCapture}
      >
        <WithReset
          className={cn(
            "relative flex w-full flex-col rounded-xl",
            "border border-app-grey-3 focus-within:border-app-primary-cta-high-contrast",
            "focus-within:shadow-tiny transition-[border,box-shadow] duration-100 ease-in-out",
            className,
          )}
        >
          <WithReset className="relative w-full min-h-0">
            <EditorContent editor={editor} />
          </WithReset>
          {bottomControls && (
            <WithReset className="flex px-[0.6rem] pb-[0.45rem] items-center">
              <WithReset className="flex items-center flex-1">{bottomControls}</WithReset>
            </WithReset>
          )}
        </WithReset>
      </div>
    );
  },
);

MentionTiptapEditor.displayName = "MentionTiptapEditor";

// Wrapper that bridges ReactRenderer props to the suggestion list.
// TipTap passes a static `props.items` from the initial `items()` call; when the # menu stays open,
// that list does not refresh when Zustand updates. Recompute from live store + query on every render.
function MentionSuggestionListWrapper(props: any) {
  const query = typeof props.query === "string" ? props.query : "";
  const liveItemsRef = props.liveItemsRef as MutableRefObject<MentionItem[]> | undefined;
  const characters = useStoryBibleStore((s) => s.charactersEntities);
  const worldbuilding = useStoryBibleStore((s) => s.worldbuildingEntries);
  const documentsRaw = useProjectStore((s) => s.contents as MentionDocumentRow[] | undefined);
  const project = useProjectStore((s) => s.project);
  const folders = useProjectStore((s) => s.project?.folders ?? []);
  const seriesBooks = useMentionSeriesBooks(project);
  const seriesBookDocuments = useMentionComposerSeriesDocsStore((s) => s.seriesBookDocuments as MentionSeriesDocumentRow[]);
  const isStoryBibleActive = useStoryBibleStore((s) => s.isActive);
  const outlineItems = useStructuredOutlineStore((s) => s.outline?.content?.items);
  const hasOutline = !!(outlineItems && outlineItems.length > 0);

  const documents = useMemo(
    () => enrichDocumentsWithSeriesBookTitles(documentsRaw ?? [], project, seriesBooks) as MentionDocumentRow[],
    [documentsRaw, seriesBooks, project],
  );

  const items = useMemo(
    () =>
      filterMentionItems(query, {
        characters,
        worldbuilding,
        documents,
        folders,
        hasOutline,
        isStoryBibleActive,
        seriesBooks,
        seriesBookDocuments,
      }),
    [query, characters, worldbuilding, documents, folders, hasOutline, isStoryBibleActive, seriesBooks, seriesBookDocuments],
  );

  if (liveItemsRef) {
    liveItemsRef.current = items;
  }

  return (
    <AnimatePresence>
      <MentionSuggestionList
        items={items}
        command={props.command}
        selectedIndex={props.selectedIndex}
        onSelectedIndexChange={props.onSelectedIndexChange}
      />
    </AnimatePresence>
  );
}

/**
 * Parse pasted plain text into Tiptap inline content nodes, converting `#Name` or legacy `@Name` patterns
 * to mention nodes when the name matches a known mention item (case-insensitive, longest match first).
 * Newlines become hardBreak nodes.
 */
function parsePastedTextWithMentions(text: string, sortedItems: MentionItem[]): any[] {
  const nodes: any[] = [];
  const lines = text.split("\n");
  const itemCandidates = sortedItems.map((item) => {
    const variants = Array.from(
      new Set(
        [
          item.mentionLabel?.trim(),
          item.display?.trim(),
          item.subtitle?.trim() ? `${item.display.trim()} (${item.subtitle.trim()})` : undefined,
        ].filter((label): label is string => !!label),
      ),
    ).sort((a, b) => b.length - a.length);
    return { item, variants };
  });

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    if (lineIndex > 0) {
      nodes.push({ type: "hardBreak" });
    }

    const line = lines[lineIndex];
    let i = 0;
    let textBuffer = "";

    while (i < line.length) {
      const ch = line[i];
      if (ch === "#" || ch === "@") {
        const remaining = line.slice(i + 1);
        let matched: MentionItem | null = null;
        let matchedLabelLength = 0;

        for (const { item, variants } of itemCandidates) {
          const matchedVariant = variants.find((label) => {
            if (!remaining.toLowerCase().startsWith(label.toLowerCase())) {
              return false;
            }
            const afterMatch = remaining.slice(label.length);
            // Only match if followed by a non-word character or end of string.
            return !afterMatch || !/\w/.test(afterMatch[0]);
          });
          if (matchedVariant) {
            if (matchedVariant.length > matchedLabelLength) {
              matched = item;
              matchedLabelLength = matchedVariant.length;
            }
          }
        }

        if (matched) {
          if (textBuffer) {
            nodes.push({ type: "text", text: textBuffer });
            textBuffer = "";
          }
          nodes.push({ type: "mention", attrs: { id: matched.id, label: matched.display, bookTitle: matched.subtitle } });
          i += 1 + matchedLabelLength;
        } else {
          textBuffer += ch;
          i++;
        }
      } else {
        textBuffer += line[i];
        i++;
      }
    }

    if (textBuffer) {
      nodes.push({ type: "text", text: textBuffer });
    }
  }

  return nodes;
}

function updatePopupPosition(popup: HTMLDivElement, props: any, options?: { postLayoutPass?: boolean }) {
  const rect = props.clientRect?.();
  if (!rect) return;

  const padding = 8;
  const viewportWidth = window.visualViewport?.width ?? document.documentElement.clientWidth ?? window.innerWidth;
  const sizingEl =
    popup.firstElementChild instanceof HTMLElement ? popup.firstElementChild : popup;
  sizingEl.style.maxWidth = `calc(100vw - ${padding * 2}px)`;
  const popupWidth = Math.max(
    sizingEl.getBoundingClientRect().width,
    sizingEl.offsetWidth,
    sizingEl.scrollWidth,
    288,
  );
  let left = rect.left;

  // If it would overflow the right edge, pull it left
  if (left + popupWidth > viewportWidth - padding) {
    left = viewportWidth - popupWidth - padding;
  }
  // Don't go off the left edge either
  if (left < padding) {
    left = padding;
  }

  popup.style.left = `${left}px`;
  popup.style.right = "auto";
  popup.style.top = `${rect.top - padding}px`;
  popup.style.transform = "translateY(-100%)";

  // First measurement can happen before ReactRenderer content fully lays out.
  // Re-clamp one frame later using final width to prevent right-edge overflow.
  if (!options?.postLayoutPass) {
    requestAnimationFrame(() => {
      updatePopupPosition(popup, props, { postLayoutPass: true });
    });
  }
}
